import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Clock, ListChecks, FileText } from "lucide-react";
import { Wordmark } from "@/components/Logo";
import CheckoutButton from "@/components/CheckoutButton";
import SamplePreviewButton from "@/components/SamplePreviewModal";
import AssessmentOffers from "@/components/AssessmentOffers";
import Guarantee from "@/components/Guarantee";
import FAQ from "@/components/FAQ";
import Footer from "@/components/Footer";

const MASK = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STEPS = [
  { icon: ShieldCheck, text: "Purchase your assessment — covered by the 7-day guarantee if eligible at checkout" },
  { icon: ListChecks, text: "Complete the 2-minute business intake (only name and email required)" },
  { icon: FileText, text: "Receive your human-reviewed report in your portal, with prioritized recommendations" },
  { icon: Clock, text: "Attend your one-on-one strategy session — booked right after intake" },
];

export default function AssessmentLandingPage() {
  const location = useLocation();
  const campaign = location.search;

  return (
    <div data-testid="assessment-landing" className="relative bg-obsidian min-h-screen">
      <header className="border-b border-gold/10 bg-obsidian/90 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
          <Link to={`/${campaign}`} data-testid="landing-home-link">
            <Wordmark />
          </Link>
          <nav className="flex items-center gap-4 sm:gap-6 text-sm">
            <Link to={`/#packages${campaign}`} data-testid="landing-nav-packages" className="hidden sm:block text-slate-300 hover:text-gold transition-colors">
              Packages
            </Link>
            <Link to={`/#products${campaign}`} data-testid="landing-nav-products" className="hidden sm:block text-slate-300 hover:text-gold transition-colors">
              Products
            </Link>
            <Link to={`/#consulting${campaign}`} data-testid="landing-nav-consulting" className="text-slate-300 hover:text-gold transition-colors">
              Consulting
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="relative px-5 sm:px-8 pt-16 sm:pt-24 pb-14 overflow-hidden">
          <div
            className="absolute inset-0 pointer-events-none"
            style={{ background: "radial-gradient(55% 55% at 70% 20%, rgba(212,175,55,0.1), transparent 70%)" }}
          />
          <div className="relative max-w-3xl">
            <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-gold mb-6">
              Business Growth Assessment
            </p>
            <h1 className="font-display font-extrabold tracking-tight text-white text-3xl sm:text-5xl leading-[1.08]">
              <MASK delay={0.1}>Understand Your Business</MASK>
              <MASK delay={0.24}>Before Your Next</MASK>
              <MASK delay={0.38}>
                <span className="text-gold-gradient">Marketing Investment.</span>
              </MASK>
            </h1>
            <p className="text-slate-300 text-base sm:text-lg mt-6 leading-relaxed" data-testid="landing-supporting-copy">
              Review your digital presence, identify potential gaps, and decide what deserves
              attention first.
            </p>

            <div className="flex flex-wrap items-center gap-4 mt-9">
              <CheckoutButton
                lookup="aih_full_assessment"
                label="Get My Assessment — $99.99"
                testid="landing-cta-assessment"
                className="px-7 py-4 rounded-full bg-gold text-obsidian font-display font-bold text-sm sm:text-base hover:bg-gold-bright hover:-translate-y-0.5 transition-all duration-300 gold-ring"
              />
              <SamplePreviewButton
                testid="landing-cta-preview"
                className="px-7 py-4 rounded-full border border-slate-500 text-slate-200 font-semibold text-sm sm:text-base hover:border-gold hover:text-gold transition-colors duration-300"
              />
            </div>

            <Link
              to={`/intake?type=express${campaign}`}
              data-testid="landing-snapshot-alt"
              className="inline-flex items-center gap-2 mt-6 text-sm text-emerald-400 hover:text-emerald-300 underline underline-offset-4 transition-colors"
            >
              Short on time? The $9.99 Business Snapshot is the automated alternative.
            </Link>
          </div>
        </section>

        <section className="px-5 sm:px-8 pb-16">
          <div className="max-w-4xl mx-auto rounded-3xl border border-slate-700/60 bg-obsidian-800/60 p-7 sm:p-10" data-testid="landing-fulfillment">
            <h2 className="font-display text-xl font-bold text-white">What happens after you purchase</h2>
            <div className="grid sm:grid-cols-2 gap-4 mt-6">
              {STEPS.map((s, i) => (
                <div key={i} className="flex gap-3.5 items-start rounded-2xl border border-slate-700/60 bg-obsidian-900/50 p-5">
                  <s.icon className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                  <p className="text-slate-300 text-sm leading-relaxed">
                    <span className="text-white font-semibold">{i + 1}.</span> {s.text}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-xs mt-5 leading-relaxed">
              The automated Snapshot is typically published to your portal within minutes of intake.
              The full assessment is human-reviewed before delivery; its review turnaround is
              confirmed with you at purchase.
            </p>
          </div>
        </section>

        <AssessmentOffers />
        <Guarantee />
        <FAQ subset={[0, 1, 2]} />
      </main>
      <Footer />
    </div>
  );
}
